import { ref, computed, type Ref, type Component } from 'vue'

/**
 * General-purpose dialog state for content that should break out of the
 * drawer and render as a centered overlay.  Any drawer card (or timeline
 * click handler) can call `openDrawerDialog()` to push content here;
 * the `DrawerDialog` component (rendered once in the AppShell) reads
 * the reactive state and shows/hides the modal.
 *
 * Two modes:
 *  1. **Plain text** — pass `title` + `body` and DrawerDialog wraps them
 *     in its own Modal chrome.
 *  2. **Component** — pass `component` (use `markRaw()`) and optional
 *     `componentProps`.  DrawerDialog renders the component directly
 *     (no extra Modal wrapper) so the component can bring its own chrome.
 *     Event handlers can be passed as `onXxx` keys inside `componentProps`.
 */

export interface DrawerDialogPayload {
  title: string
  body?: string
  maxWidth?: number
  component?: Component
  componentProps?: Record<string, any>
}

const _payload: Ref<DrawerDialogPayload | null> = ref(null)

export function useDrawerDialog() {
  const payload = computed(() => _payload.value)
  const isOpen = computed(() => _payload.value !== null)

  function openDrawerDialog(p: DrawerDialogPayload) {
    _payload.value = { ...p }
  }

  function closeDrawerDialog() {
    _payload.value = null
  }

  return { payload, isOpen, openDrawerDialog, closeDrawerDialog }
}
